<template>
    <div
        class="flex bg-white border-grey q-mt-md q-pa-md"
        v-if="!isMobileEditing"
        @click="toggleSelectCheckbox"
        :class="{ 'cursor-pointer': isMergeMode || isBulkUpdateMode }"
    >
        <div class="col">
            <div class="row">
                <!-- Status -->
                <div
                    class="col-xs-12 col-md-4"
                    :class="{ 'q-pb-lg': $q.screen.lt.md }"
                >
                    <div
                        class="col-xs-12 col-md-4 flex items-center full-height no-wrap text-h6 text-bold"
                    >
                        <q-checkbox
                            v-if="isMergeMode || isBulkUpdateMode"
                            v-model="selected"
                            color="accent"
                        />
                        <q-icon
                            name="fa-regular fa-circle-pause"
                            size="sm"
                            class="q-mr-sm"
                            color="grey"
                        >
                            <q-tooltip anchor="bottom middle" self="top middle">
                                {{ $t('Shutdown') }}
                            </q-tooltip>
                        </q-icon>
                        <div v-if="causeName" class="ellipsis">
                            {{ causeName }}
                        </div>
                        <div v-else>
                            {{ $t('Shutdown') }}
                        </div>
                    </div>
                </div>

                <div
                    class="col-xs-12 col-md-8"
                    :class="{ 'q-pt-md': $q.screen.lt.md }"
                >
                    <div class="row full-width items-center justify-center">
                        <div class="col-xs-12 col-sm-6">
                            <div class="row">
                                <!-- Started at -->
                                <div class="col-xs-12 col-sm-12 col-md-6">
                                    <div class="phase-label">
                                        {{ $t('Started at') }}
                                    </div>
                                    <div>
                                        {{
                                            getMonthDayTime(
                                                start,
                                                userLocale,
                                                localTimezoneName
                                            )
                                        }}
                                    </div>
                                </div>
                                <!-- Duration -->
                                <div class="col-xs-12 col-sm-12 col-md-6">
                                    <div
                                        class="phase-label"
                                        :class="{ 'q-mt-md': $q.screen.lt.md }"
                                    >
                                        {{ $t('Duration') }}
                                    </div>
                                    <div class="flex items-center no-wrap">
                                        <span>{{ duration }}</span>
                                        <q-chip
                                            v-if="isOngoing"
                                            color="grey"
                                            text-color="white"
                                            size="sm"
                                            :label="$t('Ongoing')"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="col-xs-12 col-sm-6"
                            :class="{ 'q-mt-md': $q.screen.lt.md }"
                        >
                            <div class="row items-center">
                                <!-- Description -->
                                <div class="col-xs-12 col-sm-12 col-md-6">
                                    <div
                                        v-if="description"
                                        :class="{
                                            'q-mt-md': $q.screen.lt.sm
                                        }"
                                    >
                                        <div class="phase-label">
                                            {{ $t('Description') }}
                                        </div>
                                        <div
                                            v-if="
                                                description.length <=
                                                descriptionMaxLength
                                            "
                                        >
                                            {{ description }}
                                        </div>
                                        <div v-else>
                                            {{
                                                description.substring(
                                                    0,
                                                    descriptionMaxLength
                                                )
                                            }}...
                                            <span
                                                @click="
                                                    $emit(
                                                        'showDescription',
                                                        description
                                                    )
                                                "
                                                class="text-accent cursor-pointer"
                                            >
                                                {{ $t('read more') }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <!-- Media -->
                                <div
                                    class="col-xs-12 col-sm-12 col-md-6"
                                    :class="{
                                        'q-mt-md': $q.screen.lt.md,
                                        'q-px-md': $q.screen.gt.sm
                                    }"
                                >
                                    <media-gallery-button
                                        :mediaIds="mediaIds"
                                        :phaseId="phaseId"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Action buttons -->
        <div class="col-auto flex flex-center">
            <div class="shutdown-buttons">
                <q-btn
                    id="edit-phase-button"
                    @click="handleEdit"
                    text-color="accent"
                    icon="fas fa-edit"
                    color="white"
                    outline
                    :disable="disableEdit"
                    size="md"
                >
                    <q-tooltip
                        v-if="uneditableMessage"
                        anchor="bottom middle"
                        self="top middle"
                    >
                        {{ uneditableMessage }}
                    </q-tooltip>
                </q-btn>
                <q-btn
                    id="split-button"
                    @click="$emit('split')"
                    :disable="disableSplit"
                    color="white"
                    :text-color="splitTextColor"
                    outline
                    size="md"
                    class="q-ml-sm"
                >
                    <q-icon style="text-align: center" name="call_split" />
                    <q-tooltip anchor="bottom middle" self="top middle">
                        <span v-if="disableSplit">
                            {{
                                $t(
                                    'This phase is too short to be split. A phase must be at least be 3 seconds long to split.'
                                )
                            }}
                        </span>
                        <span v-else> {{ $t('Split phase') }} </span>
                    </q-tooltip>
                </q-btn>
            </div>
        </div>
    </div>
    <div v-else class="row bg-white border-grey q-mt-md">
        <!-- Status column -->
        <div class="col-1 flex items-center justify-center bg-light-grey">
            <q-icon
                color="secondary"
                name="fa-solid fa-circle-pause"
                class="q-mx-none q-mb-sm"
                size="sm"
                square
                outline
            >
                <q-tooltip>
                    {{ $t('Shutdown') }}
                </q-tooltip>
            </q-icon>
        </div>
        <!-- Fields column (breaks down to 2 columns on medium devices, single column on small devices) -->
        <div class="col-8 q-pa-md">
            <banner
                v-if="!end && isMobileEditing"
                class="q-mb-md q-mr-md"
                :is-visible="!end && isMobileEditing"
                type="info"
                :message="$t(editPhaseInfoMessage)"
            />

            <!-- EDIT MODE START -->

            <div class="row q-col-gutter-x-md" v-if="isMobileEditing">
                <!-- First column of fields -->
                <div
                    class="col-xs-12 col-md-6 q-col-gutter-x-md"
                    :class="{
                        'q-col-gutter-y-lg': $q.screen.lt.md
                    }"
                >
                    <!-- Date range field -->
                    <date-time-picker
                        v-model="form.start"
                        :label="$t('Start')"
                        :disable="true"
                    />
                    <date-time-picker
                        v-model="form.end"
                        :label="$t('End')"
                        :disable="true"
                    />
                    <!-- Status field 
                        Moved to the third row to align the fields on both columns
                        because the date range fields and the product and cause
                        fields have the same space below reserved for error messages.
                    -->
                    <q-select
                        v-model="form.status"
                        :label="$t('Status')"
                        :options="statusOptions"
                        outlined
                        lazy-rules
                        :disable="
                            !isPhaseEditable(end) || isPhaseInProgress(end)
                        "
                        color="accent"
                    >
                        <!-- Option style -->
                        <template v-slot:option="scope">
                            <q-item
                                v-bind="scope.itemProps"
                                @click="scope.itemEvents"
                            >
                                <q-item-section class="row">
                                    <div>
                                        <q-icon
                                            :color="
                                                ASSET_STATUS_TO_STYLE[
                                                    scope.opt.value
                                                ]['color']
                                            "
                                            name="fas fa-circle"
                                            class="q-mr-sm"
                                        ></q-icon>
                                        {{ $t(scope.opt.label) }}
                                    </div>
                                </q-item-section>
                            </q-item>
                        </template>
                        <!-- Selected item style -->
                        <template v-slot:selected>
                            <q-icon
                                :color="form.status['color']"
                                style="margin-bottom: 2px"
                                name="fas fa-circle"
                            />
                            <div style="margin-left: 5px">
                                {{ $t(form.status['label']) }}
                            </div>
                        </template>
                    </q-select>
                </div>

                <!-- Second column of fields -->
                <div
                    class="col-xs-12 col-md-6 q-col-gutter-x-md"
                    :class="{
                        'q-mt-lg': $q.screen.lt.md
                    }"
                >
                    <!-- Product field -->
                    <table-select
                        v-if="form.status.value !== 'stopped'"
                        v-model="form.product"
                        @confirmSelection="
                            form.product = productSelectProps.selection
                        "
                        :baseURL="productSelectProps.url"
                        :queryParams="productSelectProps.queryParams"
                        :label="productSelectProps.label"
                        :dialogTitle="productSelectProps.dialogTitle"
                        :searchLabel="productSelectProps.searchLabel"
                        :rules="formRules.product"
                        row-key="id"
                        :disable="
                            !isPhaseEditable(end) || isPhaseInProgress(end)
                        "
                    >
                        <template v-slot:item="data">
                            <product-card
                                @select="
                                    productSelectProps.selection = {
                                        label: data.props.row.name,
                                        value: data.props.row.product
                                    }
                                "
                                :name="data.props.row.name"
                                :code="data.props.row.code"
                                :description="data.props.row.description"
                                :unit-measure="data.props.row.unit_measure"
                                :unit-packing="data.props.row.unit_packing"
                                :isSelected="
                                    productSelectProps.selection &&
                                    productSelectProps.selection.value ===
                                        data.props.row.product
                                "
                                :isSelectable="true"
                            />
                        </template>
                    </table-select>

                    <!-- Cause field -->
                    <cause-table-select
                        v-if="
                            form.status.value === 'changeover' ||
                            form.status.value === 'in_downtime' ||
                            form.status.value === 'stopped'
                        "
                        v-model="form.cause"
                        @confirmSelection="
                            form.cause = causeSelectProps.selection
                        "
                        :baseURL="causeSelectProps.url"
                        :queryParams="getCauseQueryParams"
                        :label="causeSelectProps.label"
                        :dialogTitle="causeSelectProps.dialogTitle"
                        :searchLabel="causeSelectProps.searchLabel"
                        :facilityId="causeSelectProps.facilityId"
                        :rules="formRules.cause"
                        row-key="id"
                        :disable="!isPhaseEditable(end)"
                        :noDataLabel="getNoShutdownCausesMessage"
                    >
                        <template v-slot:item="data">
                            <cause-card
                                @select="
                                    causeSelectProps.selection = {
                                        label: data.props.row.name,
                                        value: data.props.row.id
                                    }
                                "
                                :name="data.props.row.name"
                                :lossType="data.props.row.type_of_loss"
                                :categoryName="data.props.row.category_name"
                                :planned="data.props.row.planned"
                                :description="data.props.row.description"
                                :targetDowntime="
                                    data.props.row.target_down_time
                                "
                                :isSelected="
                                    causeSelectProps.selection &&
                                    causeSelectProps.selection.value ===
                                        data.props.row.id
                                "
                                :isSelectable="true"
                                :equipmentFailure="
                                    data.props.row.equipment_failure
                                "
                            />
                        </template>
                    </cause-table-select>
                    <!-- Description field -->
                    <q-input
                        v-model="form.description"
                        :label="$t('Description')"
                        :input-style="'min-height: 25px; height: 25px;'"
                        type="textarea"
                        outlined
                        color="accent"
                        :disable="!isPhaseEditable(end)"
                    />
                </div>
            </div>
            <div class="row q-mt-md q-mr-md q-gutter-lg" v-if="isMobileEditing">
                <div
                    v-if="isMediaUploadEnabled"
                    class="col-sm-1 col-xs-12"
                    :class="{ 'q-mb-md': $q.screen.lt.sm }"
                >
                    <!-- Add media -->
                    <media-input-manager
                        :mediaCount="
                            editableMedia.length +
                            updatedMedia.add.length -
                            updatedMedia.remove.length
                        "
                        @update:add="medias => updatedMedia.add.push(...medias)"
                    />
                </div>
                <div v-if="isMediaUploadEnabled" class="col-sm-10 col-xs-12">
                    <!-- Display media with remove button -->
                    <media-grid-manager
                        :phaseId="phaseId"
                        :existingMedias="editableMedia"
                        :newMedias="updatedMedia.add"
                        @update:removeExistingMedia="
                            mediaId => updatedMedia.remove.push(mediaId)
                        "
                        @update:removeNewMedia="
                            newMedias => (updatedMedia.add = newMedias)
                        "
                        :inline="true"
                    />
                </div>
            </div>
        </div>

        <!-- Action column -->
        <div class="col-3 flex justify-center items-center">
            <q-btn
                @click="
                    () => {
                        isMobileEditing = false;
                    }
                "
                icon="fas fa-times"
                color="negative"
                size="md"
                outline
            >
                <q-tooltip>
                    {{ $t('Cancel') }}
                </q-tooltip>
            </q-btn>
            <q-btn
                @click="submit"
                :loading="loading"
                :disable="!formHasChanged"
                icon="fas fa-check"
                color="positive"
                text-color="white"
                size="md"
                class="q-ml-md"
            >
                <q-tooltip>
                    {{ $t('Save') }}
                </q-tooltip>
            </q-btn>
        </div>
    </div>
</template>

<script>
import MediaGalleryButton from '@components/MediaGalleryButton.vue';
import MediaGridManager from '@components/MediaGridManager.vue';
import MediaInputManager from '@components/MediaInputManager.vue';
import Banner from '@components/Banner.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import CauseCard from '@components/CauseCard.vue';
import CauseTableSelect from '@components/CauseTableSelect.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import { editPhaseInfoMessage } from '@utils/getMessages.js';
import { STATUS_OPTIONS, ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import { isPhaseEditable, isPhaseInProgress } from '@utils/phaseChecker.js';
import {
    PROXIMITY_SENSOR_OPTIONS,
    PROXIMITY_SENSOR_LABELS
} from '@models/devices.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { updatePhaseMedia } from '@utils/mediaManager.js';
import { getMonthDayTime } from '@models/timeformats.js';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useNotificationsStore } from '@stores/notifications.ts';

export default {
    name: 'ShutdownPhase',
    components: {
        MediaGalleryButton,
        Banner,
        DateTimePicker,
        CauseCard,
        CauseTableSelect,
        ProductCard,
        TableSelect,
        MediaInputManager,
        MediaGridManager
    },
    emits: [
        'edit',
        'split',
        'showDescription',
        'refreshList',
        'triggerSelected'
    ],
    props: {
        facilityId: {
            type: Number,
            required: true
        },
        assetId: {
            type: Number,
            required: true
        },
        status: {
            type: String,
            required: true
        },
        phaseId: {
            type: Number,
            required: true
        },
        causeId: {
            type: Number,
            default: null
        },
        causeName: {
            type: String,
            default: null
        },
        start: {
            type: String,
            required: true
        },
        end: {
            type: String,
            default: null
        },
        duration: {
            type: String,
            required: true
        },
        isOngoing: {
            type: Boolean,
            default: false
        },
        description: {
            type: String,
            default: null
        },
        mediaIds: {
            type: Array,
            default: () => []
        },
        disableEdit: {
            type: Boolean,
            default: false
        },
        disableSplit: {
            type: Boolean,
            default: false
        },
        uneditableMessage: {
            type: String,
            default: null
        },
        splitTextColor: {
            type: String,
            default: 'grey'
        },
        descriptionMaxLength: {
            type: Number,
            default: 50
        },
        isMergeMode: {
            type: Boolean,
            default: false
        },
        isBulkUpdateMode: {
            type: Boolean,
            default: false
        },
        proximityReading: {
            type: [Number, String],
            required: false,
            default: null
        },
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    created() {
        this.initForm();
    },
    data() {
        return {
            isMobileEditing: false,
            editPhaseInfoMessage,
            statusOptions: STATUS_OPTIONS,
            ASSET_STATUS_TO_STYLE,
            PROXIMITY_SENSOR_LABELS,
            loading: false,
            form: {
                start: null,
                end: null,
                status: null,
                cause: null,
                product: null,
                description: '',
                proximityReading: null
            },
            formRules: {
                cause: [
                    val => this.validateCause(val) || this.$t('Select a cause')
                ],
                product: [
                    val =>
                        this.validateProduct(val) || this.$t('Select a product')
                ]
            },
            // selectors
            causeSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null,
                facilityId: this.facilityId
            },
            productSelectProps: {
                url: `/assets/${this.assetId}/products/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            proximitySensorOptions: PROXIMITY_SENSOR_OPTIONS,
            // media
            editableMedia: [],
            updatedMedia: {
                add: [],
                remove: []
            },
            localTimezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get query parameters for cause select.
         * In case of stopped phase, only show shutdown causes.
         * @return {Object}
         */
        getCauseQueryParams() {
            const urlParams = { ordering: 'name' };
            if (this.form.status.value === 'stopped') {
                urlParams['type_of_loss'] = 'shutdown';
            }
            return urlParams;
        },
        /**
         * Show a custom message if the selected phase is a stopped phase
         * and no shutdown causes are available.
         * @return {String}
         */
        getNoShutdownCausesMessage() {
            if (this.form.status.value === 'stopped') {
                return this.$t(
                    'No planned shutdown cause is available for this line.'
                );
            }
            return '';
        },
        /**
         * Verify the state of the MEDIA_UPLOAD feature flag
         * @return {Boolean}
         */
        isMediaUploadEnabled() {
            return this.isFeatureEnabled('MEDIA_UPLOAD');
        },
        /**
         * Enable the submit button when the user updates the form.
         * @return {Boolean}
         */
        formHasChanged() {
            return (
                this.dateToIsoString(this.start) !==
                    this.dateToIsoString(this.form.start) ||
                this.dateToIsoString(this.end) !==
                    this.dateToIsoString(this.form.end) ||
                this.status != this.form.status.value ||
                this.causeId != this.form.cause?.value ||
                this.form.product?.value !== null ||
                this.proximityReading != this.form.proximityReading ||
                this.description != this.form.description ||
                this.editableMedia.length !== this.mediaIds.length ||
                this.updatedMedia.add.length !== 0 ||
                this.updatedMedia.remove.length !== 0
            );
        },
        /**
         * Payload to send server to update phase.
         * @return {Object}
         */
        payload() {
            const status = this.form.status.value;
            const payload = {
                phase: status,
                asset: this.assetId,
                start: this.form.start,
                end: this.form.end,
                description: this.form.description,
                cause: null,
                product: null
            };

            if (status === 'running') {
                payload['product'] = this.form.product.value;
            }

            if (status === 'changeover') {
                payload['cause'] = this.form.cause.value;
                payload['product'] = this.form.product.value;
            }

            if (status === 'in_downtime') {
                if (this.form.cause) {
                    payload['cause'] = this.form.cause.value;
                }
                payload['product'] = this.form.product.value;
                payload['proximity_reading'] =
                    this.form.proximityReading === null
                        ? null
                        : this.form.proximityReading;
            }

            if (
                status === 'stopped' &&
                this.form.cause &&
                this.form.cause.value
            ) {
                payload['cause'] = this.form.cause.value;
            }

            return payload;
        },
        /**
         * Selected property for merge mode.
         * @return {Boolean}
         */
        selected: {
            get: function () {
                return this.isSelected;
            },
            set: function (newValue) {
                this.$emit('triggerSelected');
            }
        }
    },
    methods: {
        isPhaseEditable,
        isPhaseInProgress,
        isFeatureEnabled,
        updatePhaseMedia,
        getMonthDayTime,
        ...mapActions(useNotificationsStore, ['resolveNotificationAction']),
        /**
         * Handles the edit action.
         * If the screen is greater than md, emits the edit event.
         * Otherwise, opens the mobile edit dialog.
         */
        handleEdit() {
            if (this.$q.screen.gt.md) {
                this.$emit('edit');
            } else {
                this.isMobileEditing = true;
            }
        },
        /**
         * Initialize form.
         */
        initForm() {
            this.form.start = this.start;
            this.form.end = this.end;
            this.form.description = this.description;
            this.form.proximityReading =
                this.proximityReading === null ? null : this.proximityReading;
            // set status option
            this.form.status = this.statusOptions.filter(
                option => option.value === 'stopped'
            )[0];
            if (this.causeId) {
                this.form.cause = {
                    label: this.causeName,
                    value: this.causeId
                };
            }
            // Initialize editable media
            this.editableMedia = this.mediaIds || [];
        },
        /**
         * Convert a given date to ISO String
         * @param {Date} date
         * @return {String|null} - ISO String or null if date is invalid
         */
        dateToIsoString(date) {
            if (!date) {
                return null;
            }
            return new Date(date).toISOString();
        },
        /**
         * Save phase.
         */
        async submit() {
            this.loading = true;
            const errorMessage = this.$t(
                'Failed to update phase at this time. Please try again later.'
            );
            try {
                const filesToUpload = this.updatedMedia.add.map(
                    media => media.file
                );
                const mediaIdsToRemove = this.updatedMedia.remove;

                // Upload media
                if (filesToUpload.length > 0 || mediaIdsToRemove.length > 0) {
                    await updatePhaseMedia(
                        filesToUpload,
                        mediaIdsToRemove,
                        this.phaseId
                    );
                }

                // Then update the phase
                await this.$axios.put(
                    `assetphases/${this.phaseId}/`,
                    this.payload
                );

                // If cause is part of payload, resolve the downtime in the store to
                // remove notification from notifications list
                if (this.payload.cause) {
                    this.resolveNotificationAction(this.phaseUUID);
                }

                // notify user
                this.$notifySuccess(this.$t('Updated phase.'));
                // close dialog & reload list
                this.$emit('save');
                this.$emit('refreshList');
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if ('uneditable_phase' in error) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(errorMessage, error);
                }
            } finally {
                // reset media changes
                this.updatedMedia = {
                    add: [],
                    remove: []
                };
                this.loading = false;
                this.inlineEditMode = false;
            }
        },
        /**
         * Toggle select checkbox for merge and bulk update modes.
         */
        toggleSelectCheckbox() {
            if (this.isMergeMode || this.isBulkUpdateMode) {
                this.selected = !this.selected;
            }
        },
        /**
         * Retrieve proximity sensor label from proximity reading.
         * @param {int} reading
         * @return {String} - Proximity sensor label.
         */
        getProximityLabel(reading) {
            if (reading === null || reading === undefined) {
                return null;
            }
            // If the reading can not be mapped to a label, return null
            const label = PROXIMITY_SENSOR_LABELS[reading]?.label;
            return !label ? null : this.$t(label);
        }
    }
};
</script>

<style lang="scss" scoped>
.shutdown-buttons {
    width: 128px;
}

.action-buttons {
    background-color: $background-grey;
}
</style>
